/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion -- DIRECT-239681 */
import { keys, JSONCompatible } from '@direct-frontend/stdlib';
import { createReducer, createSlice } from '@reduxjs/toolkit';

import { buildCreateSelector, type CreateModulesSelector } from './selectors/buildCreateSelector';
import { bindActionCreator, type BoundActionCreator } from './bindActionCreator';
import { executeReducerBuilderCallback } from './executeReducerBuilderCallback';
import { createHydrateSliceAction } from './hydration';
import { patchSliceReducer } from './patchSliceReducer';
import type {
    ActionReducerMapBuilder,
    AnyAction,
    CreateSliceOptions,
    Slice as ReduxSlice,
    SliceCaseReducers as SliceCaseReducersRedux,
    Store,
    Reducer,
    SliceSelectors,
} from './rtk-types';
import type { GetStoreFn, InjectReducerFn } from './types';

/**
 * Тип для поля reducers в createSlice({reducers: ... }).
 * Нужен для отключения возможности написать thunk, как это позволяет @reduxjs/toolkit:
 * https://redux-toolkit.js.org/usage/migrating-rtk-2#createslicereducers-callback-syntax-and-thunk-support
 * Для поддержки этой возможности нужно научиться "биндить" созданные thunk к стору
 */
export type SliceCaseReducers<State> =
    SliceCaseReducersRedux<State> extends Record<string, infer T>
        ? Record<string, Exclude<T, { payloadCreator: any }>>
        : never;

/**
 * Тип слайса как в @reduxjs/toolkit но без возможности описать thunk в reducers
 */
type Slice<
    State = any,
    CaseReducers extends SliceCaseReducers<State> = SliceCaseReducers<State>,
    Name extends string = string,
    ReducerPath extends string = Name,
    Selectors extends SliceSelectors<State> = SliceSelectors<State>,
> = ReduxSlice<State, CaseReducers, Name, ReducerPath, Selectors>;

/**
 * Символ для приватного хранения внутренностей созданного слайса в возвращаемом значении `createSlice`.
 * Внутренности нужны для более удобного выведения типов в дженериках.
 */
export const $INTERNALS = Symbol('$INTERNALS');

/**
 * Получает название слайса из префикса для экшенов.
 * Префикс для экшенов содержит название модуля, поэтому его нужно отбросить.
 */
type ExtractSliceName<T extends string> = T extends `${string}/${infer R}` ? R : never;

/**
 * Получает подтип модульного стейта, содержащий только один слайс.
 *
 * @internal
 */
export type SliceState<S> = S extends { [$INTERNALS]: { slice: Slice<infer State, any, infer ActionTypePrefix> } }
    ? { [Key in ActionTypePrefix as ExtractSliceName<ActionTypePrefix>]: State }
    : never;

/**
 * Набор обернутых action creator'ов, которые можно вызывать без `store.dispatch()`.
 * Получается как результат вызова `bindActionCreators()`.
 *
 * @typeParam ActionCreators - Набор action creator'ов, которые будут переданы в `bindActionCreators()`.
 */
type BoundActionCreators<ActionCreatorsMap extends Slice['actions']> = {
    [Key in keyof ActionCreatorsMap]: BoundActionCreator<ActionCreatorsMap[Key]>;
};

/**
 * Привязывает набор action creator'ов к стору.
 *
 * @param getStore - Функция, возвращающая стор, к которому будут привязаны экшены.
 * @param actionCreators - Набор action creator'ов, которые будут привязаны к стору.
 * @returns Набор обернутых action creator'ов, которые можно вызывать без `store.dispatch()`.
 */
function bindActionCreators<ActionCreators extends Slice['actions']>(
    getStore: () => Store,
    actionCreators: ActionCreators
) {
    return keys(actionCreators).reduce((acc, key) => {
        const actionCreator = actionCreators[key];

        acc[key] = bindActionCreator(getStore, actionCreator!);

        return acc;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    }, {} as BoundActionCreators<ActionCreators>);
}

/**
 * Созданный слайс с привязанными экшенами и методом `addExtraReducers`.
 *
 * @typeParam State – Тип State из типа Slice.
 * @typeParam CaseReducers – Тип CaseReducers из типа Slice.
 * @typeParam Name – Тип Name из типа Slice.
 * @internal
 */
export type CreatedSlice<
    State,
    CaseReducers extends SliceCaseReducers<State>,
    Name extends string = string,
> = BoundActionCreators<Slice<State, CaseReducers, `${string}/${Name}`>['actions']> & {
    type: 'slice';
    addExtraReducers(builderCallback: (builder: ActionReducerMapBuilder<NoInfer<State>>) => void): void;
    createSelector: CreateModulesSelector<State>;
    [$INTERNALS]: {
        getStore: GetStoreFn;
        moduleName: string;
        slice: Slice<State, CaseReducers, `${string}/${Name}`>;
        sliceName: Name;
    };
};

/**
 * Возвращает название слайса модульного стора
 * @param slice Слайс модульного стора
 * @returns Название слайса
 */
export function getSliceName<State, CaseReducers extends SliceCaseReducers<State>, Name extends string = string>(
    slice: CreatedSlice<State, CaseReducers, Name>
) {
    return slice[$INTERNALS].sliceName;
}

/**
 * Произвольный созданный слайс.
 * Используется в условных типах и дженериках как правая часть extends.
 *
 * @internal
 */
export type AnyCreatedSlice = CreatedSlice<any, any, any>;

// Параметры для `buildCreateSliceOptions()`.
export type BuildCreateSliceOptions<ModuleName extends string> = {
    moduleName: ModuleName;
    getStore: GetStoreFn;
    injectReducer: InjectReducerFn;
    wrapSliceReducer?: (reducer: Reducer) => Reducer;
    /**
     * Помечает создаваемые слайсы как небезопасные для гидрации (см. `unsafeCreateSlice`).
     * Стейт таких слайсов исключается из payload гидрации в `getStateForHydration`,
     * т.к. может содержать не-JSON значения (промисы, функции, AbortController и т.п.).
     */
    unsafe?: boolean;
};

/**
 * Реестр имён небезопасных слайсов в формате `${moduleName}/${sliceName}`.
 * Заполняется при вызове `unsafeCreateSlice` и используется в `getStateForHydration`
 * для исключения таких слайсов из payload гидрации.
 */
const unsafeSliceNames = new Set<string>();

/**
 * Возвращает реестр небезопасных слайсов. Используется в `getStateForHydration`.
 * @internal
 */
export function getUnsafeSliceNames(): ReadonlySet<string> {
    return unsafeSliceNames;
}

/**
 * Сбрасывает реестр небезопасных слайсов. Используется только для тестов.
 * @internal
 */
export function cleanupUnsafeSliceNames() {
    unsafeSliceNames.clear();
}

/**
 * Функция для создания слайса в модульном сторе.
 * @deprecated
 */
export type UnsafeBuiltCreateSlice = <
    State,
    CaseReducers extends SliceCaseReducers<State>,
    Name extends string = string,
>(
    options: Omit<CreateSliceOptions<State, CaseReducers, Name>, 'extraReducers' | 'selectors'>
) => CreatedSlice<State, CaseReducers, Name>;

/**
 * Функция для создания слайса в модульном сторе.
 * @internal
 */
export type BuiltCreateSlice = <
    State extends JSONCompatible<State>,
    CaseReducers extends SliceCaseReducers<State>,
    Name extends string = string,
>(
    options: Omit<CreateSliceOptions<State, CaseReducers, Name>, 'extraReducers' | 'selectors'>
) => CreatedSlice<State, CaseReducers, Name>;

/**
 * Создает модульный метод `createSlice()`.
 *
 * Модульный `createSlice()`:
 *  1. Создает слайс
 *  2. Добавляет редьюсер в модульный стор
 *  3. Привязывает все сгенерированные экшены к модульному стору
 *  4. Добавляет метод `slice.addExtraReducers`, для добавления extraReducers в AsyncThunk'ах
 *
 * @internal
 */
export function buildCreateSlice<ModuleName extends string>(
    options: BuildCreateSliceOptions<ModuleName> & { unsafe: true }
): UnsafeBuiltCreateSlice;
export function buildCreateSlice<ModuleName extends string>(
    options: BuildCreateSliceOptions<ModuleName>
): BuiltCreateSlice;
export function buildCreateSlice<ModuleName extends string>({
    moduleName,
    getStore,
    injectReducer,
    wrapSliceReducer = reducer => reducer,
    unsafe = false,
}: BuildCreateSliceOptions<ModuleName>): BuiltCreateSlice {
    return <
        State extends JSONCompatible<State>,
        CaseReducers extends SliceCaseReducers<State>,
        Name extends string = string,
    >(
        options: Omit<CreateSliceOptions<State, CaseReducers, Name>, 'extraReducers' | 'selectors'>
    ): CreatedSlice<State, CaseReducers, Name> => {
        const slice = createSlice({
            // Префикс экшенов должен содержать moduleName, чтобы их можно было различать в «App Store».
            name: `${moduleName}/${options.name}`,
            initialState: options.initialState,
            reducers: options.reducers,
        });

        if (unsafe) {
            unsafeSliceNames.add(`${moduleName}/${options.name}`);
        }

        /**
         * Все зарегистрированные редьсеры. Новые могут добавляться через slice.addExtraReducers().
         */
        const reducers: Reducer[] = [patchSliceReducer(moduleName, slice)];

        // Все дополнительные редьюсеры нужно объединить в один редьюсер, который будет зарегистрирован в сторе.
        const composedReducer = (state: State, action: AnyAction) => {
            return reducers.reduce((state, reducer) => {
                return reducer(state, action);
            }, state);
        };

        // В сторе данные лежат по пути `options.name`, чтобы иметь удобный доступ в селекторах.
        injectReducer(options.name, wrapSliceReducer(composedReducer));

        // Вызываем гидрацию слайса на клиенте.
        if (process.env.BUILD_TARGET === 'client') {
            const hydrateSliceAction = createHydrateSliceAction(slice.name);
            getStore().dispatch(hydrateSliceAction());
        }

        /**
         * Метод slice.addExtraReducers реализует функциональность extraReducers из стандартного createSlice,
         * но позволяет делать это после создания слайса.
         *
         * Метод нужен чтобы избавиться от циклической зависимости между AsyncThunk'ами и слайсами:
         *  – Слайс использует AsyncThunk, чтобы подписаться на его экшены в `extraReducers`
         *  – AsyncThunk использует слайс, чтобы вызывать экшены в асинхронной логике
         *
         * `slice.addExtraReducers` позволяет описывать редьюсеры рядом с AsyncThunk'ом, поэтому
         * зависимость от AsyncThunk'а в слайсе исчезает.
         */
        function addExtraReducers(builderCallback: (builder: ActionReducerMapBuilder<NoInfer<State>>) => void) {
            /**
             * Формируем extraReducer, точно так же как это делает @reduxjs/toolkit.
             *
             * @see: https://github.com/reduxjs/redux-toolkit/blob/master/packages/toolkit/src/createSlice.ts#L306
             */
            const [extraReducers, actionMatchers, defaultCaseReducer] = executeReducerBuilderCallback(builderCallback);
            const extraReducer = createReducer(options.initialState, builder => {
                for (let key in extraReducers) {
                    builder.addCase(key, extraReducers[key]!);
                }

                for (let matcher of actionMatchers) {
                    builder.addMatcher(matcher.matcher, matcher.reducer);
                }

                if (defaultCaseReducer) {
                    builder.addDefaultCase(defaultCaseReducer);
                }
            });
            reducers.push(extraReducer);
        }

        // Все экшены слайса автоматически привязываются к стору, чтобы их можно было вызывать без `store.dispatch()`.
        const boundActionCreators = bindActionCreators<(typeof slice)['actions']>(getStore, slice.actions);

        return {
            ...boundActionCreators,
            type: 'slice',
            addExtraReducers,
            createSelector: buildCreateSelector<State, Name>({
                getStore,
                sliceName: options.name,
            }),
            [$INTERNALS]: {
                getStore,
                moduleName,
                slice,
                sliceName: options.name,
            },
        };
    };
}
